import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Activity, Users, Clock, CheckCircle, TrendingUp, Building, Heart, Calendar, ClipboardList } from 'lucide-react';

const NurseDashboard = () => {
  const { t } = useLanguage();
  const stats = {
    vitalsUploaded: 8,
    patientsVisited: 12,
    pendingTasks: 3,
    completedTasks: 9,
  };

  const recentActivities = [
    {
      id: '1',
      type: 'vitals',
      patient: 'Mrs. Ngozi Okeke',
      action: 'Uploaded vitals (BP, PR, FHR)',
      time: '2 hours ago',
    },
    {
      id: '2',
      type: 'visit',
      patient: 'Mrs. Fatima Bello',
      action: 'Home visit completed',
      time: '4 hours ago',
    },
    {
      id: '3',
      type: 'vitals',
      patient: 'Mrs. Adaobi Nwankwo',
      action: 'Uploaded vitals (BP, Weight)',
      time: '5 hours ago',
    },
  ];

  return (
    <div className="p-6 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl shadow-lg shadow-emerald-500/30">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t('nurseDashboard')}
              </h1>
              <p className="text-gray-600 text-sm">{t('welcomeBackNurseCHW')}</p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('vitalsUploadedToday')}</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.vitalsUploaded}</h3>
              </div>
              <div className="p-2 bg-emerald-500 rounded-lg">
                <Activity className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('patientsVisited')}</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.patientsVisited}</h3>
              </div>
              <div className="p-2 bg-green-500 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('pendingTasks')}</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.pendingTasks}</h3>
              </div>
              <div className="p-2 bg-amber-500 rounded-lg">
                <Clock className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('completedTasks')}</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.completedTasks}</h3>
              </div>
              <div className="p-2 bg-purple-500 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Recent Activities */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{t('recentActivity')}</h2>
              <div className="space-y-3">
                {recentActivities.map(activity => (
                  <div key={activity.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className={`p-2 rounded-lg ${
                      activity.type === 'vitals' ? 'bg-emerald-500' : 'bg-blue-500'
                    }`}>
                      {activity.type === 'vitals' ? (
                        <TrendingUp className="w-4 h-4 text-white" />
                      ) : (
                        <Building className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate">{activity.patient}</h4>
                      <p className="text-sm text-gray-600 mt-1">{activity.action}</p>
                      <p className="text-sm text-gray-500 mt-2">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pending Tasks */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">{t('pendingTasks')}</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900">{t('uploadVitalsFor')} Mrs. Aisha</h4>
                    <p className="text-sm text-gray-600 mt-1">{t('dueToday')}</p>
                  </div>
                  <button className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/30 font-semibold flex-shrink-0">
                    {t('upload')}
                  </button>
                </div>
                <div className="flex items-center justify-between gap-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900">{t('scheduleHomeVisit')} - Mrs. Chidinma</h4>
                    <p className="text-sm text-gray-600 mt-1">{t('thisWeek')}</p>
                  </div>
                  <button className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/30 font-semibold flex-shrink-0">
                    {t('schedule')}
                  </button>
                </div>
                <div className="flex items-center justify-between gap-4 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900">{t('followUpWith')} Dr. Yusuf - {t('patientUpdates')}</h4>
                    <p className="text-sm text-gray-600 mt-1">{t('tomorrow')}</p>
                  </div>
                  <button className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all font-semibold flex-shrink-0">
                    {t('view')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-purple-600 to-purple-500 rounded-2xl shadow-lg shadow-purple-500/30 text-white p-6">
              <h3 className="text-lg font-bold mb-4">{t('quickActions')}</h3>
              <div className="space-y-3">
                <Link to="/nurse/patient-records" className="group w-full bg-white/20 hover:bg-white/30 py-3 rounded-xl transition-all flex items-center justify-center gap-2 font-medium backdrop-blur-sm">
                  <ClipboardList className="w-5 h-5" />
                  <span>{t('patientRecords')}</span>
                </Link>
                <Link to="/nurse/home-visits" className="group w-full bg-white/20 hover:bg-white/30 py-3 rounded-xl transition-all flex items-center justify-center gap-2 font-medium backdrop-blur-sm">
                  <Building className="w-5 h-5" />
                  <span>{t('homeVisits')}</span>
                </Link>
                <Link to="/nurse/appointments" className="group w-full bg-white/20 hover:bg-white/30 py-3 rounded-xl transition-all flex items-center justify-center gap-2 font-medium backdrop-blur-sm">
                  <Calendar className="w-5 h-5" />
                  <span>{t('appointments')}</span>
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">{t('thisWeek')}</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Building className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{t('homeVisits')}</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">18</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{t('vitalsUploadedStat')}</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">42</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-pink-100 rounded-lg">
                      <Heart className="w-5 h-5 text-pink-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{t('consultations')}</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">6</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-2xl shadow-lg p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-500 rounded-xl shadow-lg flex-shrink-0">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-emerald-900 mb-2">Great Work!</h3>
                  <p className="text-sm text-emerald-700">
                    You've helped 12 mothers and children this week. Keep up the excellent work!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NurseDashboard;
